import {createSelector} from '@reduxjs/toolkit';
import {
  BUDGET_TRACKER_CONSTANTS,
  CLIENT_PERMISSION_CODES_CONSTANTS,
  ECA_PERMISSION_CODES_CONSTANTS,
  EVIDENCE_SOURCE_MANAGEMENT,
  REPORT_PERMISSION_CODES_CONSTANTS,
} from '../../constants/constants';
import {RootState} from '../../redux/store';
import {Permission} from '../../types/types';

const getPermissionEnableByPermissionCode = (
  permissions: Permission[],
  permissionCode: string
) => {
  return (
    permissions.some(
      (permission) => permission?.permissionCode === permissionCode
    ) || permissions.some((permission) => permission?.type === permissionCode)
  );
};

const getPermissionByProjectId = (state: RootState, projectID?: string) => {
  const projectId = projectID ?? state.reports.projectId ?? '';
  return state.projectPermissions.permissionsByProject?.[projectId];
};

const createPermissionSelector = (permissionCode: string) =>
  createSelector([getPermissionByProjectId], (permissions) => {
    if (!permissions) return undefined;
    return getPermissionEnableByPermissionCode(permissions, permissionCode);
  });

export const hasDocumentPreviewPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_DOCUMENT_PREVIEW
);

export const hasDocumentSummaryGenerationPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_CREATE_DOC_SUMMARY
);

export const hasTaggingDocumentPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_DOCUMENT_TAGGING
);

export const hasUploadEvidencePermission = createPermissionSelector(
  EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS
);

export const hasManageEvidencePermission = createPermissionSelector(
  EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS
);

export const hasInitiateProcessingPermission = createPermissionSelector(
  EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS
);

export const hasReportBudgetConsumptionPermission = createPermissionSelector(
  BUDGET_TRACKER_CONSTANTS.REPORT_BUDGET_CONSUMPTION
);

export const hasClientsViewPermission = createPermissionSelector(
  CLIENT_PERMISSION_CODES_CONSTANTS.CLIENTS_VIEW
);

export const hasSearchTermReportPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_SEARCH_TERM_REPORT
);

export const hasAIInvestigatePermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_AI_INVESTIGATE
);

export const hasECIIndexingPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_INDEXING
);

export const hasOverlayPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_OVERLAYS
);

export const hasExportPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_EXPORTS
);

export const hasAuditReportsPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_AUDIT_REPORTS
);

export const hasECIPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.TYPE_ECI
);

export const hasSearchesPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_SEARCHES
);

export const hasCreateDocument = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT
);

export const hasSavedSearchesPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_SAVED_SEARCHES
);

export const hasSavedSearchTemplatesPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_SAVED_SEARCH_TEMPLATES
);

export const hasEditProjectProfilePermission = createPermissionSelector(
  CLIENT_PERMISSION_CODES_CONSTANTS.EDIT_PROJECT_PROFILE
);

export const hasManageSavedSearchesPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_MANAGE_SAVED_SEARCHES
);

export const hasCreateOrEditTagFieldsPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_MANAGE_TAG_FIELDS
);

export const hasCreateOrEditCustomFieldsPermission = createPermissionSelector(
  ECA_PERMISSION_CODES_CONSTANTS.ECI_MANAGE_CUSTOM_FIELDS
);

export const hasIntakeReportViewPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_INTAKE_AUDIT_VIEW
);

export const hasProgressReportViewPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROCESSING_IN_PROGRESS_VIEW
);

export const hasProcessingSummaryReportViewPermission =
  createPermissionSelector(
    REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROCESSING_SUMMARY_VIEW
  );

export const hasInteractiveVolumeReportViewPermission =
  createPermissionSelector(
    REPORT_PERMISSION_CODES_CONSTANTS.REPORT_INTERACTIVE_VOLUME_VIEW
  );

export const hasUserSummaryReportViewPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_USER_SUMMARY_VIEW
);

export const hasProducationMetricsReportViewPermission =
  createPermissionSelector(
    REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PRODUCTION_METRICS_VIEW
  );

export const hasCustomeReportViewPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_CONCIERGE_REPORTS_VIEW
);

export const hasCustomReportUploadPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_CONCIERGE_REPORTS_CREATE
);

export const hasProjectTrackerReportViewPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROJECT_TRACKER_VIEW
);

export const hasInvoiceReportViewPermission = createPermissionSelector(
  REPORT_PERMISSION_CODES_CONSTANTS.REPORT_INVOICE_VIEW
);

export const hasCompletedMappedDataReportViewPermission =
  createPermissionSelector(
    REPORT_PERMISSION_CODES_CONSTANTS.REPORT_COMPLETED_MAPPED_DATA_VIEW
  );

export const hasProcessingExceptionsReportViewPermission =
  createPermissionSelector(
    REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROCESSING_EXCEPTIONS_VIEW
  );

export const hasManageClientSettingsPermission = createPermissionSelector(
  CLIENT_PERMISSION_CODES_CONSTANTS.CLIENTS_VIEW
);

export const hasManageUsersViewPermission = createPermissionSelector(
  CLIENT_PERMISSION_CODES_CONSTANTS.USERS_VIEW
);

export const hasManagePermissionGroupsViewPermission = createPermissionSelector(
  CLIENT_PERMISSION_CODES_CONSTANTS.MANAGE_PERMISSION_GROUPS
);
